import React, { useEffect, useState } from "react";
import { X, Smartphone, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { id: "ios", labelKey: "waitlistModal.platformIos" },
  { id: "android", labelKey: "waitlistModal.platformAndroid" },
  { id: "both", labelKey: "waitlistModal.platformBoth" },
];

/**
 * WaitlistModal — email + name + platform preference capture modal.
 * Pairs with WaitlistBanner. On submit, POSTs to /api/waitlist/signup
 * and shows a warm success state. The backend sends the confirmation
 * email via Resend asynchronously — we don't block the success state
 * on the email delivery.
 */
const WaitlistModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState("both");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyOnList, setAlreadyOnList] = useState(false);

  // Reset state each time the modal is reopened so a second signup
  // doesn't get stuck on the previous success screen.
  useEffect(() => {
    if (open) {
      setDone(false);
      setAlreadyOnList(false);
    }
  }, [open]);

  // ESC closes.
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  const canSubmit =
    name.trim().length > 0 && email.trim().includes("@") && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API}/waitlist/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          platform,
          reason: reason.trim() || null,
          // Honeypot — we don't render this field to the user; bots
          // that auto-fill every field will trip the server-side trap.
          website: "",
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setAlreadyOnList(Boolean(data.already_subscribed));
      setDone(true);
    } catch (err) {
      toast.error(t("waitlistModal.errorSave"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="waitlist-modal-backdrop"
    >
      <div
        className="relative w-full max-w-md bg-white/95 backdrop-blur-xl rounded-3xl border border-[#EADDD7] p-7 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="waitlist-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="waitlist-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("waitlistModal.closeAria")}
          className="absolute top-4 right-4 text-[#8A7D82] hover:text-[#5C4B51] p-1 rounded-full hover:bg-[#FDFBF7]"
          data-testid="waitlist-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        {!done ? (
          <form onSubmit={onSubmit}>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center">
                <Smartphone className="h-5 w-5 text-[#9B6A6C]" />
              </span>
              <div>
                <h3
                  className="text-xl font-medium text-[#2C2123] leading-tight"
                  id="waitlist-modal-title"
                >
                  {t("waitlistModal.title")}
                </h3>
                <p className="text-xs uppercase tracking-[0.18em] text-[#9B6A6C] mt-0.5">
                  {t("waitlistModal.subtitle")}
                </p>
              </div>
            </div>

            <p className="text-sm text-[#5C4B51] leading-relaxed mb-5">
              {t("waitlistModal.intro")}
            </p>

            <label className="block mb-4">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A7D82]">
                {t("waitlistModal.nameLabel")}
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                className="mt-1.5 w-full px-4 py-3 bg-white/80 rounded-xl border border-[#EADDD7] focus:border-[#9B6A6C] focus:outline-none text-[#2C2123]"
                data-testid="waitlist-name-input"
                required
              />
            </label>

            <label className="block mb-4">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A7D82]">
                {t("waitlistModal.emailLabel")}
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className="mt-1.5 w-full px-4 py-3 bg-white/80 rounded-xl border border-[#EADDD7] focus:border-[#9B6A6C] focus:outline-none text-[#2C2123]"
                data-testid="waitlist-email-input"
                required
              />
            </label>

            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2">
                {t("waitlistModal.platformLabel")}
              </div>
              <div className="flex gap-2">
                {PLATFORMS.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setPlatform(p.id)}
                    className={`flex-1 px-3 py-2.5 rounded-full text-sm transition-all border ${
                      platform === p.id
                        ? "bg-[#9B6A6C] text-white border-[#9B6A6C]"
                        : "bg-white/70 text-[#5C4B51] border-[#EADDD7] hover:border-[#D5A8A4]"
                    }`}
                    data-testid={`waitlist-platform-${p.id}`}
                  >
                    {t(p.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.18em] text-[#8A7D82] mb-2 flex items-baseline justify-between">
                <span>
                  {t("waitlistModal.reasonLabel")}{" "}
                  <span className="normal-case text-[#BDAFB3]">
                    {t("waitlistModal.reasonOptional")}
                  </span>
                </span>
                <span className="normal-case text-[11px] text-[#BDAFB3]">
                  {reason.length}/500
                </span>
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                rows={3}
                placeholder={t("waitlistModal.reasonPlaceholder")}
                className="w-full px-4 py-3 bg-white/80 rounded-xl border border-[#EADDD7] focus:border-[#9B6A6C] focus:outline-none text-[#2C2123] text-sm leading-relaxed resize-none"
                data-testid="waitlist-reason-input"
              />
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white rounded-full px-5 py-3 text-sm hover:bg-[#805557] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="waitlist-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {submitting
                ? t("waitlistModal.submitting")
                : t("waitlistModal.submit")}
            </button>
            <p className="text-[11px] text-[#8A7D82] mt-3 text-center">
              {t("waitlistModal.footer")}
            </p>
          </form>
        ) : (
          <div className="text-center py-4" data-testid="waitlist-success">
            <div className="inline-flex h-14 w-14 rounded-full bg-[#EFE6E2] items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7 text-[#5D4050]" />
            </div>
            <h3 className="text-xl font-medium text-[#2C2123] mb-2">
              {alreadyOnList
                ? t("waitlistModal.successAlready")
                : t("waitlistModal.successNew")}
            </h3>
            <p className="text-sm text-[#5C4B51] leading-relaxed mb-4">
              {alreadyOnList
                ? t("waitlistModal.successBodyAlready")
                : t("waitlistModal.successBodyNew")}
            </p>
            <p className="text-sm text-[#5C4B51] leading-relaxed mb-4">
              <Trans
                i18nKey="waitlistModal.successTryWeb"
                components={{
                  web: (
                    <a
                      href="https://trubeaute.com/"
                      className="text-[#9B6A6C] underline"
                    />
                  ),
                  strong: <span className="font-medium" />,
                }}
              />
            </p>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white rounded-full px-5 py-3 text-sm hover:bg-[#805557] transition-colors"
              data-testid="waitlist-success-close"
            >
              {t("waitlistModal.successBack")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitlistModal;
